import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as MH from 'mh-client';
import { getApiClient, resetApiClient } from '../../lib/apiClient';

export const LoginMethod = MH.LoginMethod;

export { getEmailFromToken, getToken, hasScope } from './utils';

interface PasswordlessParams {
    email: string;
    url: string;
    force?: boolean;
}

interface UsePasswordlessOptions {
    onSuccess?: (data: MH.PasswordlessResponse) => void;
    onError?: (error: Error) => void;
}

export function usePasswordless(options?: UsePasswordlessOptions) {
    const client = getApiClient();

    return useMutation({
        mutationKey: ['passwordless'],
        mutationFn: async (
            params: PasswordlessParams
        ): Promise<MH.PasswordlessResponse> => {
            const request = new MH.PasswordlessRequest(
                params.email,
                params.url,
                params.force ?? false
            );
            return await client.auth.login(request);
        },
        onSuccess: data => {
            options?.onSuccess?.(data);
        },
        onError: error => {
            options?.onError?.(error as Error);
        },
    });
}

export function useVerify() {
    const queryClient = useQueryClient();
    const client = getApiClient();

    return useMutation({
        mutationKey: ['verify'],
        mutationFn: (
            request: MH.VerifyAuthRequest
        ): Promise<MH.JsVerifyAuthResponse> => client.auth.verify(request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}

export function useProfile(options?: { enabled?: boolean }) {
    const client = getApiClient();

    return useQuery({
        queryKey: ['profile'],
        queryFn: async (): Promise<MH.ProfileResponse> => {
            return await client.auth.getProfile();
        },
        enabled: options?.enabled ?? client.isLoggedIn(),
    });
}

export function useRenewToken() {
    const client = getApiClient();

    return useMutation({
        mutationFn: async (): Promise<MH.TokenResponse> => {
            return await client.auth.renewToken();
        },
        onError: (error: Error) => {
            console.error('Token renewal failed:', error.message);
        },
    });
}

export function useLogout() {
    const queryClient = useQueryClient();
    const client = getApiClient();

    return useMutation({
        mutationFn: async () => {
            await client.logout();
        },
        onSuccess: () => {
            queryClient.clear();
            resetApiClient();
        },
    });
}

export function useIsLoggedIn(): boolean {
    const client = getApiClient();
    return client.isLoggedIn();
}

export function useKycReference() {
    const client = getApiClient();

    return useQuery({
        queryKey: ['kyc', 'reference'],
        queryFn: async (): Promise<MH.KycReferenceResponse> => {
            return await client.auth.getKycReference();
        },
        enabled: client.isLoggedIn(),
    });
}
